import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  Upload, 
  Image as ImageIcon,
  Eye,
  Star
} from 'lucide-react';
import { testimonialsAPI, getImageUrl } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  TestimonialsContainer,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  Select,
  TestimonialsList,
  TestimonialCard,
  TestimonialHeader,
  TestimonialTitle,
  RemoveTestimonialButton,
  ImageUpload,
  ImageInput,
  ImageLabel,
  ImagePreview,
  ImagePlaceholder,
  ActionButton,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  PreviewSection,
  PreviewTitle,
  PreviewContent,
  TestimonialsPreview,
  TestimonialPreviewCard,
  TestimonialPhoto,
  TestimonialPhotoPlaceholder,
  TestimonialMessage,
  TestimonialRating,
  Star as StarIcon,
  TestimonialName,
  TestimonialRole,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText
} from './TestimonialsSection.styles';

const TestimonialsSection = () => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    testimonials: []
  });
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialsAPI.get();
      if (response.status === 200) {
        const data = response.data;
        setFormData({
          testimonials: data.testimonials || []
        });
        setExistingData(data);
        
        // Set up image mapping for existing photos
        if (data.testimonials) {
          const imageMap = {};
          data.testimonials.forEach((testimonial, index) => {
            if (testimonial.image_path) {
              imageMap[index] = testimonial.image_path;
            }
          });
          setImages(imageMap);
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.message || 'Failed to load testimonials data';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const addTestimonial = () => {
    setFormData(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, {
        message: '',
        client_name: '',
        role: '',
        rating: 5
      }]
    }));
  };

  const removeTestimonial = (index) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
    
    // Remove associated image
    setImages(prev => {
      const newImages = { ...prev };
      delete newImages[index];
      return newImages;
    });
  };

  const handleTestimonialChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((testimonial, i) => 
        i === index ? { ...testimonial, [field]: value } : testimonial
      )
    }));
  };

  const handleImageChange = (index, file) => {
    if (file) {
      setImages(prev => ({
        ...prev,
        [index]: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.testimonials.length === 0) {
      setError('At least one testimonial is required');
      return;
    }

    // Validate each testimonial
    for (let i = 0; i < formData.testimonials.length; i++) {
      const testimonial = formData.testimonials[i];
      if (!testimonial.message.trim() || !testimonial.client_name.trim() || !testimonial.role.trim()) {
        setError(`Testimonial ${i + 1}: All fields are required`);
        return;
      }
      
      if (testimonial.rating < 1 || testimonial.rating > 5) {
        setError(`Testimonial ${i + 1}: Rating must be between 1 and 5`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = new FormData();
      submitData.append('testimonials', JSON.stringify(formData.testimonials));

      // Add image files
      Object.keys(images).forEach(index => {
        if (images[index] instanceof File) {
          submitData.append(`testimonial_${index}_image`, images[index]);
        }
      });

      const response = existingData 
        ? await testimonialsAPI.update(submitData)
        : await testimonialsAPI.create(submitData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'Testimonials section updated successfully!' : 'Testimonials section created successfully!');
        setExistingData(response.data);
        loadTestimonials(); // Reload to get updated data
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save testimonials section';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon key={i} filled={i < rating}>
        ★
      </StarIcon>
    ));
  };

  if (loading && !existingData) {
    return (
      <TestimonialsContainer>
        <SectionHeader>
          <Title>Testimonials Section</Title>
          <Subtitle>Loading...</Subtitle>
        </SectionHeader>
      </TestimonialsContainer>
    );
  }

  return (
    <TestimonialsContainer>
      <SectionHeader>
        <Title>Testimonials Section Management</Title>
        <Subtitle>Manage client testimonials with messages, ratings, and photos</Subtitle>
      </SectionHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Form onSubmit={handleSubmit}>
        <TestimonialsList>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <Label>Testimonials *</Label>
            <ActionButton type="button" onClick={addTestimonial}>
              <Plus size={16} />
              Add Testimonial
            </ActionButton>
          </div>

          {formData.testimonials.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>💬</EmptyStateIcon>
              <EmptyStateText>No testimonials added yet. Click "Add Testimonial" to get started.</EmptyStateText>
            </EmptyState>
          ) : (
            formData.testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <TestimonialHeader>
                  <TestimonialTitle>Testimonial {index + 1}</TestimonialTitle>
                  <RemoveTestimonialButton type="button" onClick={() => removeTestimonial(index)}>
                    <Trash2 size={14} />
                    Remove
                  </RemoveTestimonialButton>
                </TestimonialHeader>

                <FormGroup>
                  <Label htmlFor={`testimonial-${index}-message`}>Message *</Label>
                  <TextArea
                    id={`testimonial-${index}-message`}
                    value={testimonial.message}
                    onChange={(e) => handleTestimonialChange(index, 'message', e.target.value)}
                    placeholder="Client testimonial message..."
                    required
                    style={{ minHeight: '100px' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor={`testimonial-${index}-name`}>Client Name *</Label>
                  <Input
                    id={`testimonial-${index}-name`}
                    type="text"
                    value={testimonial.client_name}
                    onChange={(e) => handleTestimonialChange(index, 'client_name', e.target.value)}
                    placeholder="e.g., John Smith"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor={`testimonial-${index}-role`}>Role *</Label>
                  <Input
                    id={`testimonial-${index}-role`}
                    type="text"
                    value={testimonial.role}
                    onChange={(e) => handleTestimonialChange(index, 'role', e.target.value)}
                    placeholder="e.g., CEO, TechCorp"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor={`testimonial-${index}-rating`}>Rating *</Label>
                  <Select
                    id={`testimonial-${index}-rating`}
                    value={testimonial.rating}
                    onChange={(e) => handleTestimonialChange(index, 'rating', parseInt(e.target.value))}
                    required
                  >
                    <option value={1}>1 Star</option>
                    <option value={2}>2 Stars</option>
                    <option value={3}>3 Stars</option>
                    <option value={4}>4 Stars</option>
                    <option value={5}>5 Stars</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Photo (Optional)</Label>
                  <ImageUpload>
                    <ImageInput
                      type="file"
                      id={`testimonial-${index}-photo`}
                      accept="image/*"
                      onChange={(e) => handleImageChange(index, e.target.files[0])}
                    />
                    <ImageLabel htmlFor={`testimonial-${index}-photo`}>
                      <Upload size={16} />
                      Choose Photo
                    </ImageLabel>
                    <ImagePreview>
                      {images[index] ? (
                        images[index] instanceof File ? (
                          <img
                            src={URL.createObjectURL(images[index])}
                            alt={`Preview ${testimonial.client_name || 'testimonial'}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <img
                            src={getImageUrl(images[index])}
                            alt={testimonial.client_name || 'testimonial'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )
                      ) : (
                        <ImagePlaceholder>
                          <ImageIcon size={24} />
                          <span>No photo</span>
                        </ImagePlaceholder>
                      )}
                    </ImagePreview>
                  </ImageUpload>
                </FormGroup>
              </TestimonialCard>
            ))
          )}
          
          {formData.testimonials.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
              <ActionButton type="button" onClick={addTestimonial}>
                <Plus size={16} />
                Add Another Testimonial
              </ActionButton>
            </div>
          )}
        </TestimonialsList>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <Save size={20} />
              {existingData ? 'Update Testimonials Section' : 'Create Testimonials Section'}
            </>
          )}
        </SubmitButton>
      </Form>

      {existingData && (
        <PreviewSection>
          <PreviewTitle>
            <Eye size={20} />
            Preview
          </PreviewTitle>
          <PreviewContent>
            {existingData.testimonials && existingData.testimonials.length > 0 && (
              <TestimonialsPreview>
                {existingData.testimonials.map((testimonial, index) => (
                  <TestimonialPreviewCard key={index}>
                    <TestimonialPhoto>
                      {testimonial.image_path ? (
                        <img
                          src={getImageUrl(testimonial.image_path)}
                          alt={testimonial.client_name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <TestimonialPhotoPlaceholder>👤</TestimonialPhotoPlaceholder>
                      )}
                    </TestimonialPhoto>
                    <TestimonialMessage>"{testimonial.message}"</TestimonialMessage>
                    <TestimonialRating>
                      {renderStars(testimonial.rating)}
                    </TestimonialRating>
                    <TestimonialName>{testimonial.client_name}</TestimonialName>
                    <TestimonialRole>{testimonial.role}</TestimonialRole>
                  </TestimonialPreviewCard>
                ))}
              </TestimonialsPreview>
            )}
          </PreviewContent>
        </PreviewSection>
      )}

    </TestimonialsContainer>
  );
};

export default TestimonialsSection;
